import React, { useEffect, useState, useCallback, useMemo } from 'react';
import './AppMenu.css';
import { api } from '../api/robyBackend';
import CreateAppPopup from './CreateAppPopup';
import { useAgentLoadingStatus } from './useLoadingStatus';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Wrapper-Komponente für App-Kacheln
const SortableAppTile = ({ id, disabled, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
};

// Hilfsfunktion: Apps nach Benutzer-Präferenzen sortieren
const sortAppsWithPrefs = (apps, prefs) => {
  const { agent_order = [], favorites = [], hidden = [] } = prefs;

  // Sichtbare und versteckte Apps trennen
  const visibleApps = apps.filter((app) => !hidden.includes(app._id || app.id));
  const hiddenApps = apps.filter((app) => hidden.includes(app._id || app.id));

  // Sichtbare Apps sortieren: Favoriten zuerst, dann nach Benutzer-Reihenfolge, dann alphabetisch
  const sortedVisible = [...visibleApps].sort((a, b) => {
    const aId = a._id || a.id;
    const bId = b._id || b.id;

    const aIsFav = favorites.includes(aId);
    const bIsFav = favorites.includes(bId);

    // Favoriten kommen zuerst
    if (aIsFav && !bIsFav) return -1;
    if (!aIsFav && bIsFav) return 1;

    // Dann nach benutzerdefinierter Reihenfolge
    const aOrderIdx = agent_order.indexOf(aId);
    const bOrderIdx = agent_order.indexOf(bId);

    if (aOrderIdx !== -1 && bOrderIdx !== -1) {
      return aOrderIdx - bOrderIdx;
    }
    if (aOrderIdx !== -1) return -1;
    if (bOrderIdx !== -1) return 1;

    // Schließlich alphabetisch
    return (a.name || '').localeCompare(b.name || '', 'de', {
      sensitivity: 'base',
    });
  });

  return { visible: sortedVisible, hidden: hiddenApps };
};

const AppMenu = ({ handleNewSessionApp, toggleSidebar, isVisible }) => {
  const [apps, setApps] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCreateAppOpen, setIsCreateAppOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [appToDelete, setAppToDelete] = useState(null);
  const [appToEdit, setAppToEdit] = useState(null);
  const [isDeletePopupVisible, setIsDeletePopupVisible] = useState(false);

  // Benutzer-Präferenzen für Agenten
  const [userPrefs, setUserPrefs] = useState({
    agent_order: [],
    favorites: [],
    hidden: [],
  });
  const [showHidden, setShowHidden] = useState(false);

  // Dynamische Lade-Nachrichten für Agenten
  const { message: loadingMessage, isTransitioning } = useAgentLoadingStatus(loading);

  // Drag & Drop Sensoren
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    })
  );

  // Benutzer-Präferenzen laden
  const fetchUserPrefs = useCallback(async () => {
    try {
      const response = await api.get('/database/user-agent-preferences');
      setUserPrefs(response.data);
    } catch (error) {
      console.error('Error fetching user preferences:', error);
    }
  }, []);

  // Apps laden (extrahierte Funktion, wiederverwendbar für Polling)
  const fetchApps = async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const response = await api.get(`/database/apps`);
      const loaded = response.data || [];
      setApps(loaded);
    } catch (error) {
      console.error('Error fetching apps:', error);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  // Initiales Laden + Polling alle 30s
  useEffect(() => {
    fetchApps(true);
    fetchUserPrefs();
    const interval = setInterval(() => fetchApps(false), 30000); // 30s
    return () => clearInterval(interval);
  }, [fetchUserPrefs]);

  // Sortierte Apps basierend auf Präferenzen
  const { visible: visibleApps, hidden: hiddenApps } = useMemo(
    () => sortAppsWithPrefs(apps, userPrefs),
    [apps, userPrefs]
  );

  // Reihenfolge nach Drag & Drop aktualisieren
  const updateAgentOrder = useCallback(async (newOrder) => {
    try {
      await api.put('/database/user-agent-preferences', { agent_order: newOrder });
    } catch (error) {
      console.error('Error updating agent order:', error);
    }
  }, []);

  // Favorit umschalten
  const toggleFavorite = useCallback(async (appId, e) => {
    e.stopPropagation();
    try {
      const response = await api.patch(
        `/database/user-agent-preferences/toggle-favorite/${appId}`
      );
      setUserPrefs((prev) => ({
        ...prev,
        favorites: response.data.is_favorite
          ? [...prev.favorites, appId]
          : prev.favorites.filter((id) => id !== appId),
      }));
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  }, []);

  // Ausblenden umschalten
  const toggleHidden = useCallback(async (appId, e) => {
    e.stopPropagation();
    try {
      const response = await api.patch(
        `/database/user-agent-preferences/toggle-hidden/${appId}`
      );
      setUserPrefs((prev) => ({
        ...prev,
        hidden: response.data.is_hidden
          ? [...prev.hidden, appId]
          : prev.hidden.filter((id) => id !== appId),
      }));
    } catch (error) {
      console.error('Error toggling hidden:', error);
    }
  }, []);

  // Drag End Handler
  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (active.id !== over?.id) {
        const oldIndex = visibleApps.findIndex(
          (app) => (app._id || app.id) === active.id
        );
        const newIndex = visibleApps.findIndex(
          (app) => (app._id || app.id) === over.id
        );

        const newOrder = arrayMove(visibleApps, oldIndex, newIndex).map(
          (app) => app._id || app.id
        );

        setUserPrefs((prev) => ({ ...prev, agent_order: newOrder }));
        updateAgentOrder(newOrder);
      }
    },
    [visibleApps, updateAgentOrder]
  );

  const toggleEditMode = () =>
    setIsEditMode((prev) => {
      const next = !prev;
      if (!next) setSelectedApp(null); // Auswahl zurücksetzen wenn Edit-Modus aus
      return next;
    });

  // App auswählen
  const handleAppClick = async (app_id) => {
    if (isEditMode) {
      const appToEdit = apps.find((app) => (app._id || app.id) === app_id);
      if (appToEdit && appToEdit.not_delete === 'Standard') {
        return;
      }

      if (appToEdit) {
        const editData = {
          _id: appToEdit._id,
          name: appToEdit.name,
          color: appToEdit.app_config?.color || appToEdit.color || '#202a44',
          access: appToEdit.app_config?.access || 'public',
        };

        setAppToEdit(editData);
        setIsCreateAppOpen(true);
        setSelectedApp(app_id); // <-- Auswahl setzen, damit Overlay sichtbar wird
        if (toggleSidebar) toggleSidebar();
      }
      return;
    }

    setSelectedApp(app_id);
    try {
      handleNewSessionApp(app_id);
    } catch (error) {
      console.error('Error starting session:', error);
    }
  };

  // Normalisieren (duck-typing)
  const toFileArray = (x) => {
    if (!x) return [];
    if (typeof File !== 'undefined' && x instanceof File) return [x];
    if (typeof FileList !== 'undefined' && x instanceof FileList)
      return Array.from(x);
    if (Array.isArray(x)) return x.filter(Boolean);
    if (x && typeof x === 'object' && 'length' in x) {
      try {
        return Array.from(x);
      } catch {}
    }
    if (x && typeof x === 'object' && 'name' in x && 'size' in x) return [x];
    return [];
  };

  const handleCreateApp = async (formData) => {
    try {
      const submitData = new FormData();
      const isFD =
        typeof FormData !== 'undefined' && formData instanceof FormData;
      const name = isFD ? formData.get('name') ?? '' : formData?.name ?? '';
      const color = isFD
        ? formData.get('color') ?? '#4A4FFF'
        : formData?.color ?? '#4A4FFF';
      const access = isFD
        ? formData.get('access') ?? 'private'
        : formData?.access ?? 'private';

      submitData.append('name', name);
      submitData.append('color', color);
      submitData.append('access', access);

      // 🚩 FLAG: Custom System Prompt Feature
      const systemPrompt = isFD ? formData.get('system_prompt') : formData?.system_prompt;
      if (systemPrompt && systemPrompt.trim()) {
        submitData.append('system_prompt', systemPrompt.trim());
      }

      const rawFiles = isFD ? formData.getAll('files') : formData?.files;
      const files = toFileArray(rawFiles);
      for (const f of files) submitData.append('files', f);

      const response = await api.post('/database/create_new_app', submitData, {
        withCredentials: true,
        transformRequest: (d) => d,
      });

      let created = response.data || (isFD ? { name, color, access } : formData);
      created = { ...created, status: created.status || 'Building' };

      setApps((prev) => [...(prev ?? []), created]);
      const newAppId = created._id || created.app_id || created.id;
      if (newAppId) {
        setSelectedApp(newAppId);
        const newOrder = [newAppId, ...userPrefs.agent_order];
        setUserPrefs((prev) => ({ ...prev, agent_order: newOrder }));
        updateAgentOrder(newOrder);
        try {
          handleNewSessionApp?.(newAppId);
        } catch (e) {
          console.error(e);
        }
      }
      fetchApps(true);
    } catch (error) {
      console.error('Error creating app:', error);
      const detail = error?.response?.data?.detail;
      alert(
        `Fehler beim Erstellen der App: ${
          detail || error?.message || 'Unbekannter Fehler'
        }`
      );
    } finally {
      setIsCreateAppOpen(false);
    }
  };

  const handleUpdateApp = async (formData /* ist bereits FormData */) => {
    try {
      const response = await api.post(
        `/database/apps/${appToEdit._id}`,
        formData,
        {
          withCredentials: true,
          transformRequest: (d) => d,
        }
      );

      await fetchApps(true);
      let created = response.data || formData;
      created = { ...created, status: created.status || 'Building' };

      setApps((prev) => [...(prev ?? []), created]);
      const newAppId = created._id || created.id;
      if (newAppId) {
        setSelectedApp(newAppId);
        try {
          handleNewSessionApp?.(newAppId);
        } catch (e) {
          console.error(e);
        }
      }
      fetchApps(true);
      setIsEditMode(false);
    } catch (error) {
      console.error('Error updating app:', error);
      console.error('Response data:', JSON.stringify(error?.response?.data));
      const detail = error?.response?.data?.detail;
      const detailStr = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map(d => `${d.loc?.join('.')}: ${d.msg}`).join(', ')
          : JSON.stringify(detail);
      alert(
        `Fehler beim Aktualisieren der App: ${
          detailStr || error?.message || 'Unbekannter Fehler'
        }`
      );
    } finally {
      setIsCreateAppOpen(false);
    }
  };

  const handleDeleteButtonClick = (e, app) => {
    e.stopPropagation();
    if (app.not_delete === 'Standard') return;
    setAppToDelete(app);
    setIsDeletePopupVisible(true);
  };

  const confirmDeleteApp = async () => {
    try {
      await api.patch(`/database/apps/${appToDelete._id}/delete`);
      setApps((prevApps) =>
        prevApps.filter((app) => app._id !== appToDelete._id)
      );
    } catch (error) {
      console.error('Error deleting app:', error);
      const errorMsg =
        error.response?.data?.detail ||
        error.message ||
        'Unbekannter Fehler';
      alert(`Fehler beim Löschen der App: ${errorMsg}`);
    } finally {
      setIsDeletePopupVisible(false);
      setAppToDelete(null);
    }
  };

  const cancelDeleteApp = () => {
    setIsDeletePopupVisible(false);
    setAppToDelete(null);
  };

  return (
    <div className="app-menu-container">
      <div className="app-menu-header">
        <h2 className="app-menu-title">Meine Agenten</h2>
        <div className="menu-items">
          <button
            onClick={() => {
              setIsCreateAppOpen(true);
              setAppToEdit(null);
              if (toggleSidebar) toggleSidebar();
            }}
            className="add-app-button"
          >
            <img
              src={process.env.PUBLIC_URL + '/add.png'}
              alt="Neuen Agenten erstellen"
              className="add-icon"
            />
          </button>
          <button
            onClick={toggleEditMode}
            className={`edit-button ${isEditMode ? 'active' : ''}`}
          >
            <img
              src={
                process.env.PUBLIC_URL +
                (isEditMode ? '/edit_white.png' : '/edit_black.png')
              }
              alt="Bearbeiten"
              className="edit-icon"
            />
          </button>
        </div>
      </div>

      {/* Ladeanzeige jetzt HIER, oberhalb der App-Liste */}
      {loading && (
        <div className="app-menu-loading">
          <div className="agent-loading-container">
            <div className="agent-loading-dots">
              <span className="dot"></span>
              <span className="dot"></span>
              <span className="dot"></span>
            </div>
            <span className={`agent-loading-text${isTransitioning ? ' transitioning' : ''}`}>
              {loadingMessage}<span className="loading-dots-text"></span>
            </span>
          </div>
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <div className="app-menu">
          {!loading && (
            <>
              {apps && apps.length > 0 ? (
                <SortableContext
                  items={visibleApps.map((app) => app._id || app.id)}
                  strategy={rectSortingStrategy}
                  disabled={isEditMode}
                >
                  {visibleApps.map((app) => {
                    const appId = app._id || app.id;
                    const isFavorite = userPrefs.favorites.includes(appId);

                    return (
                      <SortableAppTile
                        key={appId}
                        id={appId}
                        disabled={isEditMode || app.status === 'Building'}
                      >
                        <div
                          className={`appTileContainer ${
                            app.status === 'Building' ? 'building' : ''
                          } ${isEditMode ? 'edit-mode' : ''} ${
                            isEditMode && selectedApp === appId ? 'selected' : ''
                          } ${isFavorite ? 'favorite' : ''}`}
                          data-not-deletable={app.not_delete === 'Standard'}
                          onClick={() =>
                            app.status !== 'Building' && handleAppClick(appId)
                          }
                          onMouseEnter={() =>
                            isEditMode && setSelectedApp(appId)
                          }
                          onMouseLeave={() => isEditMode && setSelectedApp(null)}
                          onFocus={() => isEditMode && setSelectedApp(appId)}
                          onBlur={() => isEditMode && setSelectedApp(null)}
                          role="button"
                          tabIndex={app.status === 'Building' ? -1 : 0}
                          onKeyDown={(e) =>
                            e.key === 'Enter' &&
                            app.status !== 'Building' &&
                            handleAppClick(appId)
                          }
                        >
                          <div
                            className="appTile"
                            style={
                              app.app_config?.color || app.color
                                ? {
                                    backgroundColor:
                                      app.app_config?.color || app.color,
                                  }
                                : {}
                            }
                          >
                            {app.status === 'Building' ? (
                              <div className="loading-gear"></div>
                            ) : (
                              <div className="icon">
                                <img
                                  src={
                                    app.logo?.startsWith('http')
                                      ? app.logo
                                      : process.env.PUBLIC_URL + '/' + app.logo
                                  }
                                  alt={app.name}
                                  className="icon"
                                />
                              </div>
                            )}

                            {/* Favoriten-Stern – nur im Edit-Modus */}
                            {isEditMode && (
                              <button
                                className={`favoriteButton ${
                                  isFavorite ? 'active' : ''
                                }`}
                                onClick={(e) => toggleFavorite(appId, e)}
                                aria-label={
                                  isFavorite
                                    ? 'Aus Favoriten entfernen'
                                    : 'Zu Favoriten hinzufügen'
                                }
                                title={
                                  isFavorite
                                    ? 'Aus Favoriten entfernen'
                                    : 'Als Favorit markieren'
                                }
                              >
                                <span className="favoriteIcon">
                                  {isFavorite ? '★' : '☆'}
                                </span>
                              </button>
                            )}

                            {/* Delete Button im Edit-Modus */}
                            {isEditMode && app.not_delete !== 'Standard' && (
                              <button
                                className="deleteAppButton"
                                onClick={(e) => handleDeleteButtonClick(e, app)}
                                aria-label={`${app.name} löschen`}
                                title="Agent löschen"
                              >
                                ×
                              </button>
                            )}

                            {/* Ausblenden-Button im Edit-Modus – für alle Agenten */}
                            {isEditMode && (
                              <button
                                className="hideAppButton"
                                onClick={(e) => toggleHidden(appId, e)}
                                aria-label="Agent ausblenden"
                                title="Agent ausblenden"
                              >
                                ⊘
                              </button>
                            )}

                            {/* Edit-Overlay nur wenn ausgewählt im Edit-Modus */}
                            {isEditMode &&
                              selectedApp === appId &&
                              app.not_delete !== 'Standard' && (
                                <div className="editModeOverlay">
                                  <img
                                    src={
                                      process.env.PUBLIC_URL + '/edit_white.png'
                                    }
                                    alt="Bearbeiten"
                                  />
                                </div>
                              )}
                          </div>
                          <div className="appTitle">{app.name}</div>
                        </div>
                      </SortableAppTile>
                    );
                  })}
                </SortableContext>
              ) : (
                <div
                  className="no-apps-container"
                  style={{
                    gridColumn: '1 / -1',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '80%',
                    marginLeft: '10%',
                    marginRight: '10%',
                    marginTop: '40px',
                    marginBottom: '40px',
                  }}
                >
                  <p
                    className="no-apps-hint"
                    style={{
                      textAlign: 'center',
                      marginBottom: '30px',
                    }}
                  >
                    Aktuell sind noch keine Apps verfügbar. Gern geben wir Zugang
                    oder stellen unsere Apps in einer Demo vor:
                    <a href="mailto:mail@robyflow.com"> mail@robyflow.com</a>
                  </p>
                  <button
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      margin: 0,
                      cursor: 'pointer',
                    }}
                    className="appTileContainer"
                    onClick={() => {
                      setIsCreateAppOpen(true);
                      setAppToEdit(null);
                      if (toggleSidebar) toggleSidebar();
                    }}
                    aria-label="Neue App erstellen"
                  >
                    <div className="appTileNew">
                      <div className="icon">
                        <img
                          src={process.env.PUBLIC_URL + '/add.png'}
                          alt={'Neue App'}
                          className="icon"
                        />
                      </div>
                    </div>
                    <div className="appTitle">Neuen Agenten erstellen</div>
                  </button>
                </div>
              )}

              {/* Neuen Agent erstellen Button */}
              {apps && apps.length > 0 && !isEditMode && (
                <button
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    margin: 0,
                    cursor: 'pointer',
                  }}
                  className="appTileContainer"
                  onClick={() => {
                    setIsCreateAppOpen(true);
                    setAppToEdit(null);
                    if (toggleSidebar) toggleSidebar();
                  }}
                  aria-label="Neue App erstellen"
                >
                  <div className="appTileNew">
                    <div className="icon">
                      <img
                        src={process.env.PUBLIC_URL + '/add.png'}
                        alt={'Neue App'}
                        className="icon"
                      />
                    </div>
                  </div>
                  <div className="appTitle">Neuen Agenten erstellen</div>
                </button>
              )}

              {/* Ausgeblendete Agenten Sektion */}
              {hiddenApps.length > 0 && (
                <div className="hidden-section">
                  <button
                    className="hidden-section-toggle"
                    onClick={() => setShowHidden(!showHidden)}
                  >
                    <span>Ausgeblendet ({hiddenApps.length})</span>
                    <span className="toggle-icon">
                      {showHidden ? '▲' : '▼'}
                    </span>
                  </button>

                  {showHidden && (
                    <div className="hidden-apps-grid">
                      {hiddenApps.map((app) => {
                        const appId = app._id || app.id;
                        return (
                          <div
                            className="appTileContainer hidden-app"
                            key={appId}
                          >
                            <div
                              className="appTile"
                              style={
                                app.app_config?.color || app.color
                                  ? {
                                      backgroundColor:
                                        app.app_config?.color || app.color,
                                    }
                                  : {}
                              }
                            >
                              <div className="icon">
                                <img
                                  src={
                                    app.logo?.startsWith('http')
                                      ? app.logo
                                      : process.env.PUBLIC_URL + '/' + app.logo
                                  }
                                  alt={app.name}
                                  className="icon"
                                />
                              </div>

                              {/* Wieder einblenden Button */}
                              <button
                                className="showAppButton"
                                onClick={(e) => toggleHidden(appId, e)}
                                aria-label="Agent wieder anzeigen"
                                title="Agent wieder anzeigen"
                              >
                                ⊕
                              </button>
                            </div>
                            <div className="appTitle">{app.name}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </DndContext>

      <CreateAppPopup
        isOpen={isCreateAppOpen}
        onClose={() => {
          setIsCreateAppOpen(false);
          setAppToEdit(null);
          if (toggleSidebar) toggleSidebar();
        }}
        onSubmit={appToEdit ? handleUpdateApp : handleCreateApp}
        editMode={!!appToEdit}
        initialData={appToEdit}
      />

      {isDeletePopupVisible && (
        <div className="deletePopupOverlay">
          <div className="deletePopup">
            <p>Möchten Sie den Agenten "{appToDelete?.name}" wirklich löschen?</p>
            <div className="popupButtons">
              <button className="confirmButton" onClick={confirmDeleteApp}>
                Löschen
              </button>
              <button className="cancelButton" onClick={cancelDeleteApp}>
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppMenu;
